/**
 * API client utilities for HTTP communication with the server.
 * Framework-agnostic — provides typed request/response helpers.
 * Actual fetch implementation is platform-specific.
 */
// ─── Default Config ─────────────────────────────────────────────────
export const defaultApiConfig = {
    baseUrl: '/api',
    timeoutMs: 15_000,
    getAccessToken: () => null,
    onTokenRefresh: async () => null,
    onAuthFailure: () => { },
};
// ─── URL Helpers ────────────────────────────────────────────────────
/**
 * Build a full URL from base, path, and query params.
 */
export function buildUrl(baseUrl, path, params) {
    const url = `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    if (!params || Object.keys(params).length === 0)
        return url;
    const paramPairs = Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');
    return `${url}?${paramPairs}`;
}
/**
 * Build request headers including auth token.
 */
export function buildHeaders(config, options) {
    const headers = {
        'Accept': 'application/json',
        ...options.headers,
    };
    if (options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    if (!options.skipAuth) {
        const token = config.getAccessToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
}
// ─── Error Handling ─────────────────────────────────────────────────
/**
 * Create a typed ApiError from a response status and body.
 */
export function createApiError(status, body) {
    return {
        status,
        code: body?.error?.code ?? 'UNKNOWN_ERROR',
        message: body?.error?.message ?? `Request failed with status ${status}`,
        details: body?.error?.details,
    };
}
/**
 * Whether an error is a network/timeout error (vs server error).
 */
export function isNetworkError(error) {
    if (error instanceof TypeError && error.message.includes('fetch'))
        return true;
    if (error instanceof Error && error.name === 'AbortError')
        return true;
    return false;
}
/**
 * Whether a status code indicates the request should be retried.
 */
export function isRetryableStatus(status) {
    return status === 429 || status === 502 || status === 503 || status === 504;
}
/**
 * Whether a status is an auth error (token expired).
 */
export function isAuthError(status) {
    return status === 401;
}
// ─── Typed API Endpoints ────────────────────────────────────────────
/**
 * Pre-built request options for common API calls.
 */
export const endpoints = {
    // Auth
    login: (email, password) => ({
        method: 'POST',
        path: '/auth/login',
        body: { email, password },
        skipAuth: true,
    }),
    register: (username, email, password) => ({
        method: 'POST',
        path: '/auth/register',
        body: { username, email, password },
        skipAuth: true,
    }),
    refreshToken: (refreshToken) => ({
        method: 'POST',
        path: '/auth/refresh',
        body: { refreshToken },
        skipAuth: true,
    }),
    logout: (refreshToken) => ({
        method: 'POST',
        path: '/auth/logout',
        body: { refreshToken },
    }),
    // User
    getProfile: () => ({
        method: 'GET',
        path: '/users/me',
    }),
    updateProfile: (updates) => ({
        method: 'PATCH',
        path: '/users/me',
        body: updates,
    }),
    // Matches
    getMatches: (page = 1, limit = 20) => ({
        method: 'GET',
        path: '/matches',
        params: { page, limit },
    }),
    getMatch: (matchId) => ({
        method: 'GET',
        path: `/matches/${matchId}`,
    }),
    // Leaderboard
    getLeaderboard: (page = 1, limit = 20) => ({
        method: 'GET',
        path: '/leaderboard',
        params: { page, limit },
        skipAuth: true,
    }),
    getMyRank: () => ({
        method: 'GET',
        path: '/leaderboard/me',
    }),
};
//# sourceMappingURL=api.js.map